import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Mail, Send, FileText, CheckCircle, Clock, Users, 
  Search, RefreshCw, Eye, Building2, Truck, Plus,
  MessageSquare, Target, TrendingUp, AlertCircle,
  ChevronRight, Inbox, Megaphone, MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagingCenter = () => {
  // State for recipients
  const [brokers, setBrokers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedBrokers, setSelectedBrokers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  
  // State for templates
  const [brokerTemplates, setBrokerTemplates] = useState([]);
  const [driverTemplates, setDriverTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templatePreview, setTemplatePreview] = useState(null);
  
  // State for stats and campaigns
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  
  // UI state
  const [activeTab, setActiveTab] = useState('brokers');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [campaignDetailOpen, setCampaignDetailOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // State options
  const [brokerStates, setBrokerStates] = useState([]);
  const [driverStates, setDriverStates] = useState([]);
  const [stateCounts, setStateCounts] = useState({ brokers: [], drivers: [] });
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentFilter, setEquipmentFilter] = useState('');
  
  // Counts for "Send to ALL" feature
  const [filteredBrokerCount, setFilteredBrokerCount] = useState(0);
  const [filteredDriverCount, setFilteredDriverCount] = useState(0);
  
  // Load opportunity fields (for driver outreach)
  const [loadOpportunity, setLoadOpportunity] = useState({
    origin: '',
    destination: '',
    distance: '',
    rate: ''
  });
  
  // Custom template form
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    subject: '',
    body: '',
    target_type: 'broker'
  });

  const fetchBrokers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (stateFilter) params.append('state', stateFilter);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/brokers?${params.toString()}`);
      setBrokers(response.data);
      
      // Fetch total count for "Send to ALL" with current filters
      const countParams = new URLSearchParams();
      if (statusFilter) countParams.append('status', statusFilter);
      if (stateFilter) countParams.append('state', stateFilter);
      const countResponse = await axios.get(`${API}/brokers/count?${countParams.toString()}`);
      setFilteredBrokerCount(countResponse.data?.total || 0);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  }, [searchQuery, statusFilter, stateFilter]);

  const fetchDrivers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (stateFilter) params.append('state', stateFilter);
      if (equipmentFilter) params.append('equipment_type', equipmentFilter);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/drivers?${params.toString()}`);
      setDrivers(response.data);
      
      // Fetch total count for "Send to ALL" with current filters
      const countParams = new URLSearchParams();
      if (statusFilter) countParams.append('status', statusFilter);
      if (stateFilter) countParams.append('state', stateFilter);
      if (equipmentFilter) countParams.append('equipment_type', equipmentFilter);
      const countResponse = await axios.get(`${API}/drivers/count?${countParams.toString()}`);
      setFilteredDriverCount(countResponse.data?.total || 0);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  }, [statusFilter, stateFilter, equipmentFilter]);

  const fetchStateOptions = async () => {
    try {
      const [brokerStatesRes, driverStatesRes, brokerCountsRes, driverCountsRes, equipmentRes] = await Promise.all([
        axios.get(`${API}/brokers/states`),
        axios.get(`${API}/drivers/states`),
        axios.get(`${API}/brokers/count-by-state`),
        axios.get(`${API}/drivers/count-by-state`),
        axios.get(`${API}/drivers/equipment-types`)
      ]);
      setBrokerStates(brokerStatesRes.data?.states || brokerStatesRes.data || []);
      setDriverStates(driverStatesRes.data?.states || driverStatesRes.data || []);
      setStateCounts({
        brokers: brokerCountsRes.data?.states || brokerCountsRes.data || [],
        drivers: driverCountsRes.data?.states || driverCountsRes.data || []
      });
      setEquipmentTypes(equipmentRes.data?.equipment_types || equipmentRes.data || []);
    } catch (error) {
      console.error('Error fetching state options:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const [brokerRes, driverRes, customRes] = await Promise.all([
        axios.get(`${API}/email/templates`),
        axios.get(`${API}/email/driver-templates`).catch(() => ({ data: { templates: [] } })),
        axios.get(`${API}/email/custom-templates`).catch(() => ({ data: { templates: [] } }))
      ]);
      setBrokerTemplates(brokerRes.data?.templates || brokerRes.data || []);
      setDriverTemplates(driverRes.data?.templates || driverRes.data || []);
      setCustomTemplates(customRes.data?.templates || customRes.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/messaging/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/email/all-campaigns?limit=20`);
      setCampaigns(response.data?.campaigns || response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchBrokers().catch(e => console.error('fetchBrokers error:', e)),
          fetchDrivers().catch(e => console.error('fetchDrivers error:', e)),
          fetchTemplates().catch(e => console.error('fetchTemplates error:', e)),
          fetchStats().catch(e => console.error('fetchStats error:', e)),
          fetchCampaigns().catch(e => console.error('fetchCampaigns error:', e)),
          fetchStateOptions().catch(e => console.error('fetchStateOptions error:', e))
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchBrokers, fetchDrivers]);

  useEffect(() => {
    if (activeTab === 'brokers') {
      fetchBrokers();
    } else {
      fetchDrivers();
    }
  }, [searchQuery, statusFilter, stateFilter, equipmentFilter, activeTab, fetchBrokers, fetchDrivers]);

  const loadTemplatePreview = async (templateId, type) => {
    try {
      const endpoint = type === 'broker' 
        ? `${API}/email/templates/${templateId}`
        : `${API}/email/driver-templates/${templateId}`;
      const response = await axios.get(endpoint);
      setTemplatePreview(response.data);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const toggleSelectBroker = (brokerId) => {
    setSelectedBrokers(prev => 
      prev.includes(brokerId) 
        ? prev.filter(id => id !== brokerId)
        : [...prev, brokerId]
    );
  };

  const toggleSelectDriver = (driverId) => {
    setSelectedDrivers(prev => 
      prev.includes(driverId) 
        ? prev.filter(id => id !== driverId)
        : [...prev, driverId]
    );
  };

  const selectAllBrokers = () => {
    if (selectedBrokers.length === brokers.length) {
      setSelectedBrokers([]);
    } else {
      setSelectedBrokers(brokers.map(b => b.id));
    }
  };

  const selectAllDrivers = () => {
    if (selectedDrivers.length === drivers.length) {
      setSelectedDrivers([]);
    } else {
      setSelectedDrivers(drivers.map(d => d.id));
    }
  };

  // Send to ALL brokers (background job)
  const sendToAllBrokers = async (maxEmails = null) => {
    if (!selectedTemplate) {
      toast.error('Select an email template');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/email/outreach-all-brokers`, {
        template_id: selectedTemplate,
        status_filter: statusFilter || null,
        state_filter: stateFilter || null,
        batch_size: 100,
        max_emails: maxEmails
      });
      
      toast.success(response.data.message);
      fetchStats();
      fetchCampaigns();
      
      // Start polling for progress
      pollCampaignProgress(response.data.campaign_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start campaign');
    } finally {
      setSending(false);
    }
  };

  // Send to ALL drivers (background job)
  const sendToAllDrivers = async (maxEmails = null) => {
    if (!selectedTemplate) {
      toast.error('Select an email template');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/email/outreach-all-drivers`, {
        template_id: selectedTemplate,
        status_filter: statusFilter || null,
        state_filter: stateFilter || null,
        batch_size: 100,
        max_emails: maxEmails,
        ...loadOpportunity
      });
      
      toast.success(response.data.message);
      fetchStats();
      fetchCampaigns();
      
      // Start polling for progress
      pollCampaignProgress(response.data.campaign_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start campaign');
    } finally {
      setSending(false);
    }
  };

  // Poll campaign progress
  const [campaignProgress, setCampaignProgress] = useState(null);
  
  const pollCampaignProgress = async (campaignId) => {
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/email/campaign-progress/${campaignId}`);
        setCampaignProgress(response.data);
        
        if (response.data.status === 'completed') {
          toast.success(`Campaign completed! ${response.data.sent} sent, ${response.data.failed} failed`);
          setCampaignProgress(null);
          fetchStats();
          fetchCampaigns();
        } else {
          setTimeout(poll, 3000); // Poll every 3 seconds
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    };
    poll();
  };

  const sendBrokerOutreach = async () => {
    if (selectedBrokers.length === 0) {
      toast.error('Select at least one broker');
      return;
    }
    if (!selectedTemplate) {
      toast.error('Select an email template');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/email/outreach`, {
        broker_ids: selectedBrokers,
        template_id: selectedTemplate
      });
      
      toast.success(`Sent ${response.data.sent} emails, ${response.data.failed} failed`);
      setSelectedBrokers([]);
      fetchBrokers();
      fetchStats();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const sendDriverOutreach = async () => {
    if (selectedDrivers.length === 0) {
      toast.error('Select at least one driver');
      return;
    }
    if (!selectedTemplate) {
      toast.error('Select an email template');
      return;
    }

    setSending(true);
    try {
      const payload = {
        driver_ids: selectedDrivers,
        template_id: selectedTemplate,
        ...loadOpportunity
      };
      
      const response = await axios.post(`${API}/email/driver-outreach`, payload);
      
      toast.success(`Sent ${response.data.sent} emails, ${response.data.failed} failed`);
      setSelectedDrivers([]);
      fetchDrivers();
      fetchStats();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const createCustomTemplate = async () => {
    if (!newTemplate.name || !newTemplate.subject || !newTemplate.body) {
      toast.error('Fill in all template fields');
      return;
    }

    try {
      await axios.post(`${API}/email/custom-templates`, newTemplate);
      toast.success('Template created successfully');
      setCreateTemplateOpen(false);
      setNewTemplate({ name: '', subject: '', body: '', target_type: 'broker' });
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const viewCampaignDetails = async (campaign) => {
    try {
      const response = await axios.get(`${API}/email/campaigns/${campaign.id}`);
      setSelectedCampaign(response.data);
      setCampaignDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load campaign details');
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <Card className="glass-card" data-testid={`msg-stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-black font-heading text-foreground mt-1">{value?.toLocaleString() || 0}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const getCurrentTemplates = () => {
    if (activeTab === 'brokers') {
      return [...brokerTemplates, ...customTemplates.filter(t => t.target_type === 'broker')];
    }
    return [...driverTemplates, ...customTemplates.filter(t => t.target_type === 'driver')];
  };

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="messaging-center">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Messaging Center</h1>
            <p className="text-muted-foreground mt-2">Mass email campaigns to brokers and owner operators</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { fetchStats(); fetchCampaigns(); }} variant="outline" data-testid="refresh-messaging">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={createTemplateOpen} onOpenChange={setCreateTemplateOpen}>
              <DialogTrigger asChild>
                <Button className="glow-button" data-testid="create-template-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl glass-card">
                <DialogHeader>
                  <DialogTitle>Create Custom Template</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Template Name</Label>
                      <Input
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                        placeholder="e.g., Holiday Special"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Target Audience</Label>
                      <select
                        value={newTemplate.target_type}
                        onChange={(e) => setNewTemplate({ ...newTemplate, target_type: e.target.value })}
                        className="w-full mt-2 px-4 py-2 rounded-lg bg-background border border-border text-foreground"
                      >
                        <option value="broker">Brokers</option>
                        <option value="driver">Drivers/Owner Operators</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label>Email Subject</Label>
                    <Input
                      value={newTemplate.subject}
                      onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                      placeholder="e.g., Special Offer from Tempus Dispatch"
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Variables: {'{{company_name}}'}, {'{{contact_name}}'} for brokers | {'{{driver_name}}'} for drivers
                    </p>
                  </div>
                  <div>
                    <Label>Email Body (HTML supported)</Label>
                    <Textarea
                      value={newTemplate.body}
                      onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                      placeholder="Enter your email content here..."
                      className="mt-2 min-h-[200px] font-mono text-sm"
                    />
                  </div>
                  <Button 
                    className="w-full glow-button" 
                    onClick={createCustomTemplate}
                  >
                    Create Template
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard icon={Mail} title="Total Sent" value={stats.total_emails_sent} color="#3B82F6" />
            <StatCard 
              icon={Building2} 
              title="Brokers Reached" 
              value={stats.brokers_contacted} 
              subtitle={`${stats.brokers_unreached?.toLocaleString()} remaining`}
              color="#8B5CF6" 
            />
            <StatCard 
              icon={Truck} 
              title="Drivers Reached" 
              value={stats.drivers_contacted} 
              subtitle={`${stats.drivers_unreached?.toLocaleString()} remaining`}
              color="#10B981" 
            />
            <StatCard icon={Megaphone} title="Campaigns" value={stats.total_campaigns} color="#F59E0B" />
            <StatCard icon={FileText} title="Templates" value={stats.custom_templates} subtitle="Custom" color="#EC4899" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Recipient Selection */}
          <div className="lg:col-span-2 space-y-4">
            <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setSelectedTemplate(''); }}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="brokers" className="flex items-center gap-2" data-testid="brokers-tab">
                  <Building2 className="w-4 h-4" />
                  Brokers ({stats?.total_brokers?.toLocaleString() || 0})
                </TabsTrigger>
                <TabsTrigger value="drivers" className="flex items-center gap-2" data-testid="drivers-tab">
                  <Truck className="w-4 h-4" />
                  Owner Operators ({stats?.total_drivers?.toLocaleString() || 0})
                </TabsTrigger>
              </TabsList>

              {/* Filters */}
              <Card className="glass-card mt-4">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder={activeTab === 'brokers' ? "Search brokers..." : "Search drivers..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="recipient-search"
                      />
                    </div>
                    <select
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                      className="px-4 py-2 rounded-lg bg-background border border-border text-foreground text-sm min-w-[150px]"
                      data-testid="state-filter"
                    >
                      <option value="">All States</option>
                      {(activeTab === 'brokers' ? stateCounts.brokers : stateCounts.drivers).map((item) => (
                        <option key={item.state} value={item.state}>
                          {item.state} ({item.count?.toLocaleString()})
                        </option>
                      ))}
                    </select>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-4 py-2 rounded-lg bg-background border border-border text-foreground text-sm"
                      data-testid="status-filter"
                    >
                      <option value="">All Status</option>
                      {activeTab === 'brokers' ? (
                        <>
                          <option value="new">New (Not Contacted)</option>
                          <option value="contacted">Already Contacted</option>
                          <option value="active">Active</option>
                        </>
                      ) : (
                        <>
                          <option value="available">Available</option>
                          <option value="assigned">Assigned</option>
                        </>
                      )}
                    </select>
                    {activeTab === 'drivers' && (
                      <select
                        value={equipmentFilter}
                        onChange={(e) => setEquipmentFilter(e.target.value)}
                        className="px-4 py-2 rounded-lg bg-background border border-border text-foreground text-sm"
                        data-testid="equipment-filter"
                      >
                        <option value="">All Equipment</option>
                        {equipmentTypes.length > 0 ? (
                          equipmentTypes.map((type) => (
                            <option key={type.type || type} value={type.type || type}>{type.type || type}</option>
                          ))
                        ) : (
                          <>
                            <option value="sprinter_van">Sprinter Van</option>
                            <option value="cargo_van">Cargo Van</option>
                            <option value="box_truck">Box Truck</option>
                            <option value="hotshot">Hotshot</option>
                            <option value="dry_van">Dry Van</option>
                            <option value="reefer">Reefer</option>
                            <option value="flatbed">Flatbed</option>
                            <option value="step_deck">Step Deck</option>
                            <option value="conestoga">Conestoga</option>
                            <option value="lowboy">Lowboy</option>
                            <option value="rgn">RGN</option>
                            <option value="double_drop">Double Drop</option>
                            <option value="curtain_side">Curtain Side</option>
                            <option value="tanker">Tanker</option>
                            <option value="hopper">Hopper</option>
                            <option value="dump_truck">Dump Truck</option>
                            <option value="power_only">Power Only</option>
                          </>
                        )}
                      </select>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Brokers Tab */}
              <TabsContent value="brokers">
                <Card className="glass-card">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-heading flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-primary" />
                        Select Brokers ({selectedBrokers.length} selected)
                      </CardTitle>
                      {brokers.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={selectAllBrokers} data-testid="select-all-brokers">
                          {selectedBrokers.length === brokers.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : brokers.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No brokers found</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {brokers.map((broker) => (
                          <div
                            key={broker.id}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                              selectedBrokers.includes(broker.id) 
                                ? 'bg-primary/10 ring-1 ring-primary' 
                                : 'bg-background/50 hover:bg-accent/50'
                            }`}
                            onClick={() => toggleSelectBroker(broker.id)}
                            data-testid={`broker-item-${broker.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedBrokers.includes(broker.id)}
                              onChange={() => {}}
                              className="w-4 h-4"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{broker.company_name}</p>
                              <p className="text-xs text-muted-foreground truncate">{broker.email}</p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              broker.status === 'new' ? 'bg-blue-500/15 text-blue-500' :
                              broker.status === 'contacted' ? 'bg-amber-500/15 text-amber-500' :
                              'bg-emerald-500/15 text-emerald-500'
                            }`}>
                              {broker.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Drivers Tab */}
              <TabsContent value="drivers">
                <Card className="glass-card">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-heading flex items-center gap-2">
                        <Truck className="w-5 h-5 text-primary" />
                        Select Owner Operators ({selectedDrivers.length} selected)
                      </CardTitle>
                      {drivers.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={selectAllDrivers} data-testid="select-all-drivers">
                          {selectedDrivers.length === drivers.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : drivers.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No drivers found</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {drivers.map((driver) => (
                          <div
                            key={driver.id}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                              selectedDrivers.includes(driver.id) 
                                ? 'bg-primary/10 ring-1 ring-primary' 
                                : 'bg-background/50 hover:bg-accent/50'
                            }`}
                            onClick={() => toggleSelectDriver(driver.id)}
                            data-testid={`driver-item-${driver.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedDrivers.includes(driver.id)}
                              onChange={() => {}}
                              className="w-4 h-4"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{driver.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {driver.email} • {driver.home_base || 'Unknown location'}
                              </p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              driver.status === 'available' ? 'bg-emerald-500/15 text-emerald-500' :
                              'bg-amber-500/15 text-amber-500'
                            }`}>
                              {driver.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Send Panel */}
          <div className="space-y-4">
            {/* Template Selection */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Email Template
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Select Template <span className="text-red-500">*</span></Label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className={`w-full mt-2 px-4 py-2 rounded-lg bg-background border text-foreground ${
                      !selectedTemplate ? 'border-amber-500/50' : 'border-border'
                    }`}
                    data-testid="template-select"
                  >
                    <option value="">-- Choose Template --</option>
                    {getCurrentTemplates().map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  {!selectedTemplate && (
                    <p className="text-xs text-amber-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Select a template to enable sending
                    </p>
                  )}
                </div>
                
                {selectedTemplate && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => loadTemplatePreview(selectedTemplate, activeTab === 'brokers' ? 'broker' : 'driver')}
                    data-testid="preview-template"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Template
                  </Button>
                )}

                {/* Load Opportunity Fields (for driver outreach) */}
                {activeTab === 'drivers' && selectedTemplate === 'load_opportunity' && (
                  <div className="space-y-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-xs font-semibold text-emerald-400 uppercase">Load Details</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Origin</Label>
                        <Input 
                          placeholder="e.g., Atlanta, GA"
                          value={loadOpportunity.origin}
                          onChange={(e) => setLoadOpportunity({...loadOpportunity, origin: e.target.value})}
                          className="mt-1 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Destination</Label>
                        <Input 
                          placeholder="e.g., Miami, FL"
                          value={loadOpportunity.destination}
                          onChange={(e) => setLoadOpportunity({...loadOpportunity, destination: e.target.value})}
                          className="mt-1 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Distance (miles)</Label>
                        <Input 
                          placeholder="e.g., 650"
                          value={loadOpportunity.distance}
                          onChange={(e) => setLoadOpportunity({...loadOpportunity, distance: e.target.value})}
                          className="mt-1 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Rate ($)</Label>
                        <Input 
                          placeholder="e.g., 1,850"
                          value={loadOpportunity.rate}
                          onChange={(e) => setLoadOpportunity({...loadOpportunity, rate: e.target.value})}
                          className="mt-1 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-border space-y-3">
                  {/* Campaign Progress */}
                  {campaignProgress && (
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-blue-400">CAMPAIGN IN PROGRESS</span>
                        <span className="text-xs text-blue-400">{campaignProgress.progress_percent}%</span>
                      </div>
                      <div className="w-full bg-blue-900/30 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${campaignProgress.progress_percent}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-blue-300 mt-2">
                        {campaignProgress.sent?.toLocaleString()} sent / {campaignProgress.total?.toLocaleString()} total
                      </p>
                    </div>
                  )}

                  {/* Send to Selected */}
                  <Button 
                    className="w-full"
                    variant="outline"
                    onClick={activeTab === 'brokers' ? sendBrokerOutreach : sendDriverOutreach}
                    disabled={sending || campaignProgress || (activeTab === 'brokers' ? selectedBrokers.length === 0 : selectedDrivers.length === 0)}
                    data-testid="send-outreach"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send to {activeTab === 'brokers' ? selectedBrokers.length : selectedDrivers.length} Selected
                  </Button>

                  {/* Current Filter Summary */}
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-primary" />
                      <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                        Active Filters
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type:</span>
                        <span className="font-medium text-foreground">
                          {activeTab === 'brokers' ? 'Brokers/Shippers' : 'Drivers/Owner-Operators'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span className="font-medium text-foreground">
                          {statusFilter ? (
                            <span className="px-2 py-0.5 rounded bg-primary/20 text-primary text-xs">
                              {statusFilter === 'new' ? 'New (Not Contacted)' : 
                               statusFilter === 'contacted' ? 'Already Contacted' :
                               statusFilter === 'active' ? 'Active' :
                               statusFilter === 'available' ? 'Available' :
                               statusFilter === 'assigned' ? 'Assigned' : statusFilter}
                            </span>
                          ) : 'All'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">State:</span>
                        <span className="font-medium text-foreground">
                          {stateFilter ? (
                            <span className="px-2 py-0.5 rounded bg-primary/20 text-primary text-xs">
                              {stateFilter}
                            </span>
                          ) : 'All States'}
                        </span>
                      </div>
                      {activeTab === 'drivers' && equipmentFilter && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Equipment:</span>
                          <span className="px-2 py-0.5 rounded bg-primary/20 text-primary text-xs">
                            {equipmentFilter}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 mt-2 border-t border-primary/20">
                        <span className="text-muted-foreground font-medium">Total Matching:</span>
                        <span className="font-bold text-lg text-primary">
                          {activeTab === 'brokers' 
                            ? filteredBrokerCount.toLocaleString() 
                            : filteredDriverCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Send to ALL - Main Action Button */}
                  <Button 
                    className={`w-full text-base py-6 ${selectedTemplate ? 'glow-button' : 'opacity-50 cursor-not-allowed'}`}
                    onClick={() => activeTab === 'brokers' ? sendToAllBrokers() : sendToAllDrivers()}
                    disabled={sending || campaignProgress || !selectedTemplate}
                    data-testid="send-all-outreach"
                  >
                    {sending ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Starting Campaign...
                      </>
                    ) : (
                      <>
                        <Megaphone className="w-5 h-5 mr-2" />
                        Send to ALL {activeTab === 'brokers' 
                          ? filteredBrokerCount.toLocaleString() 
                          : filteredDriverCount.toLocaleString()} {activeTab === 'brokers' ? 'Brokers' : 'Drivers'}
                      </>
                    )}
                  </Button>
                  
                  {/* Warning if no template selected */}
                  {!selectedTemplate && (
                    <p className="text-xs text-center text-amber-500 flex items-center justify-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Please select an email template above to enable this button
                    </p>
                  )}
                  
                  {/* Descriptive text under button */}
                  {(statusFilter || stateFilter || equipmentFilter) && (
                    <p className="text-xs text-center text-muted-foreground">
                      Sending to {statusFilter ? `"${statusFilter}" ` : 'all '} 
                      {activeTab === 'brokers' ? 'brokers' : 'drivers'}
                      {stateFilter ? ` in ${stateFilter}` : ''}
                      {equipmentFilter ? ` with ${equipmentFilter}` : ''}
                    </p>
                  )}

                  {/* Quick send options */}
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={() => activeTab === 'brokers' ? sendToAllBrokers(1000) : sendToAllDrivers(1000)}
                      disabled={sending || campaignProgress || !selectedTemplate}
                    >
                      First 1,000
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="flex-1 text-xs"
                      onClick={() => activeTab === 'brokers' ? sendToAllBrokers(10000) : sendToAllDrivers(10000)}
                      disabled={sending || campaignProgress || !selectedTemplate}
                    >
                      First 10,000
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Campaigns */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Recent Campaigns
                </CardTitle>
              </CardHeader>
              <CardContent>
                {campaigns.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No campaigns yet</p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {campaigns.map((campaign) => (
                      <div 
                        key={campaign.id}
                        className="p-3 rounded-lg bg-background/50 hover:bg-accent/50 cursor-pointer transition-colors"
                        onClick={() => viewCampaignDetails(campaign)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {campaign.target_type === 'drivers' ? (
                              <Truck className="w-3 h-3 text-emerald-500" />
                            ) : (
                              <Building2 className="w-3 h-3 text-purple-500" />
                            )}
                            <span className="text-xs text-muted-foreground">
                              {new Date(campaign.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            campaign.status === 'completed' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-amber-500/15 text-amber-500'
                          }`}>
                            {campaign.status}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-foreground">
                          {campaign.sent_count} sent / {campaign.failed_count} failed
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Template Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto glass-card">
            <DialogHeader>
              <DialogTitle>Template Preview: {templatePreview?.name}</DialogTitle>
            </DialogHeader>
            {templatePreview && (
              <div className="space-y-4">
                <div>
                  <Label>Subject</Label>
                  <p className="text-sm text-foreground mt-1 p-2 bg-background rounded">{templatePreview.subject}</p>
                </div>
                <div>
                  <Label>Body</Label>
                  <div 
                    className="mt-1 p-4 bg-white text-black rounded border"
                    dangerouslySetInnerHTML={{ __html: templatePreview.body }}
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Campaign Detail Dialog */}
        <Dialog open={campaignDetailOpen} onOpenChange={setCampaignDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto glass-card">
            <DialogHeader>
              <DialogTitle>Campaign Details</DialogTitle>
            </DialogHeader>
            {selectedCampaign && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <p className="text-sm">{new Date(selectedCampaign.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <p className="text-sm">{selectedCampaign.status}</p>
                  </div>
                  <div>
                    <Label>Sent</Label>
                    <p className="text-sm font-bold text-emerald-500">{selectedCampaign.sent_count}</p>
                  </div>
                  <div>
                    <Label>Failed</Label>
                    <p className="text-sm font-bold text-red-500">{selectedCampaign.failed_count}</p>
                  </div>
                </div>
                {selectedCampaign.emails && selectedCampaign.emails.length > 0 && (
                  <div>
                    <Label>Recipients</Label>
                    <div className="mt-2 max-h-60 overflow-y-auto space-y-2">
                      {selectedCampaign.emails.slice(0, 50).map((email, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-background/50 rounded text-sm">
                          <span className="truncate">{email.recipient_name || email.broker_company || email.recipient_email}</span>
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MessagingCenter;
